import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
} from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { CriteriaId } from "~/types/enums/CriteriaId";
import { EvaluationMethod } from "~/types/enums/EvaluationMethod";
import { EvaluationMetric, EvaluationMetrics } from "~/types/EvaluationMetric";

import { useCollection } from "vuefire";

export const useEvaluationMetrics = () => {
  const db = useFirestore();

  const evaluationMetricsRef = computed(() => {
    return collection(db, `/evaluationMetrics`);
  });

  const evaluationMetricRef = computed(() => {
    return (id: string) => {
      return doc(db, `/evaluationMetrics/${id}`);
    };
  });

  const evaluationMetricsQuery = query(
    evaluationMetricsRef.value,
    orderBy("name", "asc")
  );

  const evaluationMetricsStream = useCollection<EvaluationMetric>(
    evaluationMetricsQuery
  );

  const fetchEvaluationMetrics = async (): Promise<EvaluationMetrics> => {
    const metricQuery = query(
      evaluationMetricsRef.value,
      orderBy("name", "asc")
    );
    const snapshot = await getDocs(metricQuery);
    const evaluationMetrics = snapshot.docs.map(
      (doc) => ({ ...doc.data(), id: doc.id } as EvaluationMetric)
    );

    return evaluationMetrics;
  };

  const fetchEvaluationMetric = async (
    id: string
  ): Promise<EvaluationMetric> => {
    const ref = evaluationMetricRef.value(id);
    const doc = await getDoc(ref);
    return {
      ...(doc.data() as EvaluationMetric),
      id: doc.id,
    };
  };

  const saveEvaluationMetric = async (evaluationMetric: EvaluationMetric) => {
    if (evaluationMetric.id) {
      const ref = evaluationMetricRef.value(evaluationMetric.id);
      const data = {
        ...evaluationMetric,
      };

      delete data.id;
      // Rollout is saved separately with revision checks. Never overwrite it from
      // a stale metric editor. Replace each supplied top-level config field fully.
      delete data.rolloutStrategy;
      delete data.rolloutRevision;
      await setDoc(ref, data, { mergeFields: Object.keys(data) });
      return evaluationMetric.id;
    } else {
      const ref = evaluationMetricsRef.value;
      var response = await addDoc(ref, evaluationMetric);
      return response.id;
    }
  };

  const deleteEvaluationMetric = async (evaluationMetric: EvaluationMetric) => {
    if (!evaluationMetric.id) return;

    const ref = evaluationMetricRef.value(evaluationMetric.id);
    await deleteDoc(ref);
  };

  const generateDefaultCriteria = () => {
    return {
      uid: uuidv4(),
      id: CriteriaId.clause,
      shouldShowInstances: false,
      shouldShowToStudents: false,
      shouldHideExtraInstances: false,
      relevantInstances: [],
      text: "TBD",
      expandByDefault: false,
      forceOpen: false,
      highlightInstances: false,
      evaluationMethod: EvaluationMethod.all,
      acceptWordList: false,
      acceptParagraphPoints: false,
      manualInstanceReview: false,
      isMechanics: false,
      enableTypeSelection: false,
      preserveInstanceFormatting: false,
      useWordsToIgnore: false,
      isValidByDefault: false,
      options: {},
      instanceLabelOverride: "",
      isManual: false,
      isVisible: false,
      reversePassFail: false,
      hidePassLabel: false,
      allowOverride: false,
      criteria: [],
    };
  };

  return {
    evaluationMetricsStream,
    fetchEvaluationMetrics,
    fetchEvaluationMetric,
    saveEvaluationMetric,
    deleteEvaluationMetric,
    generateDefaultCriteria,
  };
};
